/*
  Smooth accordions (GH2)
  - Rows slide open and closed instead of jumping.
  - Only one row per section is open at a time (add data-accordion-multiple to a
    wrapper to allow several, or data-accordion-group to set your own group).
  - When a row above closes, the page scroll is adjusted so the row you
    clicked stays where it is on screen.
  - Filter dropdowns fade and slide instead of popping.
  Applies to: <details> inside .accordion (Collapsible content / FAQ, product
  accordions), product disclosures, collection filters, and any <details
  data-accordion>. Add data-accordion-ignore to opt something out.
*/
(() => {
  if (window.gh2Accordion) return;

  const DURATION = 350;
  const EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
  const SELECTOR = [
    '.accordion details',
    'details.disclosures__details',
    'details.facets__disclosure',
    'details.facets__disclosure-vertical',
    'details[data-accordion]',
  ].join(',');
  const GROUP = '[data-accordion-group], [data-product-extras], .shopify-section';

  const running = new WeakMap();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const isManaged = (details) =>
    details instanceof HTMLDetailsElement && details.matches(SELECTOR) && !details.closest('[data-accordion-ignore]');

  const summaryOf = (details) => Array.from(details.children).find((child) => child.tagName === 'SUMMARY');

  const panelsOf = (details) => Array.from(details.children).filter((child) => child.tagName !== 'SUMMARY');

  const isPopup = (details) => details.classList.contains('disclosure-has-popup');

  const isOpen = (details) => details.open && details.dataset.accordionState !== 'closing';

  const setExpanded = (details, expanded) => {
    const summary = summaryOf(details);
    if (summary) summary.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  };

  const stop = (details) => {
    const state = running.get(details);
    if (!state) return;
    state.cancelled = true;
    state.animations.forEach((animation) => animation.cancel());
    running.delete(details);
  };

  const finish = (details, opening) => {
    if (!opening) details.open = false;
    details.dataset.accordionState = opening ? 'open' : 'closed';
    details.style.overflow = '';
  };

  // Height of the row when only its title shows, in the units the CSS height uses.
  const closedHeight = (details) => {
    const summary = summaryOf(details);
    const style = getComputedStyle(details);
    const summaryStyle = summary ? getComputedStyle(summary) : null;
    let height = summary ? summary.getBoundingClientRect().height : 0;
    if (summaryStyle) height += parseFloat(summaryStyle.marginTop) + parseFloat(summaryStyle.marginBottom);
    if (style.boxSizing !== 'content-box') {
      height +=
        parseFloat(style.paddingTop) +
        parseFloat(style.paddingBottom) +
        parseFloat(style.borderTopWidth) +
        parseFloat(style.borderBottomWidth);
    }
    return height;
  };

  const currentHeight = (details) => {
    const style = getComputedStyle(details);
    let height = details.getBoundingClientRect().height;
    if (style.boxSizing === 'content-box') {
      height -=
        parseFloat(style.paddingTop) +
        parseFloat(style.paddingBottom) +
        parseFloat(style.borderTopWidth) +
        parseFloat(style.borderBottomWidth);
    }
    return height;
  };

  const animateHeight = (details, opening) => {
    const wasRunning = running.has(details);
    // Measure with overflow hidden so the content's bottom margin is counted the same
    // way for the whole animation (otherwise the row jumps a few pixels at the start).
    details.style.overflow = 'hidden';
    const startHeight = currentHeight(details);
    stop(details);

    details.dataset.accordionState = opening ? 'opening' : 'closing';
    if (opening) details.open = true;
    const endHeight = opening ? currentHeight(details) : closedHeight(details);

    if (motion.matches || Math.abs(endHeight - startHeight) < 1) {
      finish(details, opening);
      return;
    }

    const options = { duration: DURATION, easing: EASE, fill: 'forwards' };
    const animations = [
      details.animate([{ height: `${startHeight}px` }, { height: `${endHeight}px` }], options),
    ];
    if (!wasRunning) {
      panelsOf(details).forEach((panel) => {
        animations.push(
          panel.animate(opening ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0 }], options)
        );
      });
    }

    const state = { animations, cancelled: false };
    running.set(details, state);
    animations[0].onfinish = () => {
      if (state.cancelled) return;
      finish(details, opening);
      running.delete(details);
      animations.forEach((animation) => animation.cancel());
    };
  };

  const animatePopup = (details, opening) => {
    const panel = panelsOf(details)[0];
    stop(details);
    details.style.overflow = '';
    details.dataset.accordionState = opening ? 'opening' : 'closing';
    if (opening) details.open = true;
    if (!panel || motion.matches) {
      finish(details, opening);
      return;
    }

    const shown = { opacity: 1, transform: 'translateY(0)' };
    const hidden = { opacity: 0, transform: 'translateY(-0.8rem)' };
    const animation = panel.animate(opening ? [hidden, shown] : [shown, hidden], {
      duration: DURATION,
      easing: EASE,
      fill: 'forwards',
    });
    const state = { animations: [animation], cancelled: false };
    running.set(details, state);
    animation.onfinish = () => {
      if (state.cancelled) return;
      finish(details, opening);
      running.delete(details);
      animation.cancel();
    };
  };

  // Keep the clicked row still on screen while a row above it closes.
  const holdInPlace = (element) => {
    const startTop = element.getBoundingClientRect().top;
    const endTime = performance.now() + DURATION + 60;
    const step = () => {
      const shift = element.getBoundingClientRect().top - startTop;
      if (Math.abs(shift) >= 1) window.scrollBy({ top: shift, left: 0, behavior: 'instant' });
      if (performance.now() < endTime) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  const close = (details) => {
    if (!isManaged(details) || !isOpen(details)) return;
    setExpanded(details, false);
    if (isPopup(details)) animatePopup(details, false);
    else animateHeight(details, false);
  };

  const closeOthers = (details) => {
    if (isPopup(details) || details.closest('[data-accordion-multiple]')) return [];
    const group = details.closest(GROUP);
    if (!group) return [];
    const others = Array.from(group.querySelectorAll(SELECTOR)).filter(
      (other) =>
        other !== details &&
        isManaged(other) &&
        !isPopup(other) &&
        isOpen(other) &&
        !other.contains(details) &&
        !details.contains(other) &&
        other.closest(GROUP) === group
    );
    others.forEach(close);
    return others;
  };

  const open = (details) => {
    if (!isManaged(details) || isOpen(details)) return;
    const summary = summaryOf(details);
    const closedOthers = closeOthers(details);
    const closedAbove = closedOthers.some(
      (other) => other.compareDocumentPosition(details) & Node.DOCUMENT_POSITION_FOLLOWING
    );
    if (closedAbove && summary && !motion.matches) holdInPlace(summary);
    setExpanded(details, true);
    if (isPopup(details)) animatePopup(details, true);
    else animateHeight(details, true);
  };

  const toggle = (details) => (isOpen(details) ? close(details) : open(details));

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button > 0) return;
    const summary = event.target.closest('summary');
    if (!summary) return;
    const details = summary.parentElement;
    if (!isManaged(details) || summaryOf(details) !== summary) return;
    // Let links and controls placed inside a title work as normal.
    const control = event.target.closest('a[href], button, input, select, textarea');
    if (control && summary.contains(control)) return;
    event.preventDefault();
    toggle(details);
  });

  // The product extras section has its own "one open at a time" code that snaps the
  // other rows shut. This script already closes them smoothly, so keep that code out of it.
  window.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (isManaged(details) && details.closest('[data-product-extras]')) event.stopPropagation();
    },
    true
  );

  window.gh2Accordion = { open, close, toggle };
})();
